import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config/api";

export default function SpotBidPage() {
  const { spotId, token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    company_email: "",
    price: "",
    currency: "EUR",
    fuel_included: "included",
    fuel_pct: "",
    multistop_included: "excluded",
    free_time_hours: "6",
    rate_per_started_hour: "20",
    layday_after_hours: "6",
    layday_rate: "250",
    validity: "30 days",
    notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/spot-public/${spotId}/${token}`);
        setInfo(res.data);
      } catch {
        setError("This spot rate request link is invalid or has expired.");
      }
    })();
  }, [spotId, token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/spot-public/${spotId}/${token}/quote`, {
        ...form,
        service_type: info?.service_type,
        fuel_included: form.fuel_included === "included",
        multistop_included: form.multistop_included === "included",
      });
      setSubmitted(true);
    } catch {
      setError("Could not submit your quote. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md text-center" data-testid="spot-bid-error">
          <p className="text-slate-700">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md text-center" data-testid="spot-bid-success">
          <div className="text-4xl mb-3">✅</div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Thank you!</h1>
          <p className="text-slate-600">Your rate has been submitted to Europacific. We will be in touch shortly.</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Loading…</div>;
  }

  const label = "text-xs font-medium text-slate-500";
  const inp = "w-full border rounded-lg px-3 py-2 text-sm";

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-xl px-6 py-5">
          <h1 className="text-xl font-bold">Spot Rate Request</h1>
          <p className="text-emerald-50 text-sm">Europacific d.o.o. — please quote your best rate</p>
        </div>

        <div className="bg-white px-6 py-5 border-x">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Transport details</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Detail k="From" v={info.from_location} />
            <Detail k="To" v={info.to_location} />
            <Detail k="Empty return" v={info.empty_location || "-"} />
            <Detail k="Service type" v={info.service_type} />
            <Detail k="Pickup date" v={info.pickup_date || "-"} />
            <Detail k="Reference" v={info.pf_number || "-"} />
          </div>
          {info.description && (
            <div className="mt-3 text-sm">
              <div className={label}>Description</div>
              <div className="text-slate-700">{info.description}</div>
            </div>
          )}
          {info.cargo && Object.values(info.cargo).some((v) => v !== null && v !== "" && v !== false) && (
            <div className="mt-4" data-testid="spot-bid-cargo">
              <h2 className="text-sm font-semibold text-slate-700 mb-2">Cargo details</h2>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {info.cargo.packages ? <Detail k="Packages" v={`${info.cargo.packages} ${info.cargo.package_type || ""}`.trim()} /> : (info.cargo.package_type ? <Detail k="Package type" v={info.cargo.package_type} /> : null)}
                {info.cargo.weight_kg ? <Detail k="Gross weight" v={`${info.cargo.weight_kg} kg`} /> : null}
                {info.cargo.ldm ? <Detail k="Loading meters" v={`${info.cargo.ldm} LDM`} /> : null}
                {info.cargo.volume_cbm ? <Detail k="Volume" v={`${info.cargo.volume_cbm} cbm`} /> : null}
                {info.cargo.dimensions ? <Detail k="Dimensions" v={info.cargo.dimensions} /> : null}
                {info.cargo.stackable != null ? <Detail k="Stackable" v={info.cargo.stackable ? "Yes" : "No"} /> : null}
                {info.cargo.adr ? <Detail k="ADR / Dangerous" v={`Yes${info.cargo.adr_class ? " — " + info.cargo.adr_class : ""}`} /> : null}
              </div>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="bg-white rounded-b-xl border px-6 py-5 space-y-4" data-testid="spot-bid-form">
          <h2 className="text-sm font-semibold text-slate-700">Your quote</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={label}>Company name *</label>
              <input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inp} data-testid="bid-company-name" />
            </div>
            <div>
              <label className={label}>Company email *</label>
              <input required type="email" value={form.company_email} onChange={(e) => set("company_email", e.target.value)} className={inp} data-testid="bid-company-email" />
            </div>
            <div>
              <label className={label}>Price (EUR) per {info.service_type} *</label>
              <input required type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="bid-price" />
            </div>
            <div>
              <label className={label}>Fuel</label>
              <div className="flex gap-2">
                <select value={form.fuel_included} onChange={(e) => set("fuel_included", e.target.value)} className={inp}>
                  <option value="excluded">Excluded</option>
                  <option value="included">Included</option>
                </select>
                <input type="number" step="0.1" placeholder="%" value={form.fuel_pct} onChange={(e) => set("fuel_pct", e.target.value)} className="w-24 border rounded-lg px-3 py-2 text-sm" data-testid="bid-fuel-pct" />
              </div>
            </div>
          </div>

          <div className="border-t pt-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Annotations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={label}>Multistop</label>
                <select value={form.multistop_included} onChange={(e) => set("multistop_included", e.target.value)} className={inp}>
                  <option value="excluded">Excluded</option>
                  <option value="included">Included</option>
                </select>
              </div>
              <div>
                <label className={label}>Free time for unloading (hours)</label>
                <input type="number" step="0.5" value={form.free_time_hours} onChange={(e) => set("free_time_hours", e.target.value)} className={inp} />
              </div>
              <div>
                <label className={label}>Then EUR per started hour</label>
                <input type="number" step="0.01" value={form.rate_per_started_hour} onChange={(e) => set("rate_per_started_hour", e.target.value)} className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={label}>Layday after (hours)</label>
                  <input type="number" step="1" value={form.layday_after_hours} onChange={(e) => set("layday_after_hours", e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={label}>Layday (EUR)</label>
                  <input type="number" step="0.01" value={form.layday_rate} onChange={(e) => set("layday_rate", e.target.value)} className={inp} />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={label}>Validity</label>
              <input value={form.validity} onChange={(e) => set("validity", e.target.value)} placeholder="e.g. 30 days" className={inp} />
            </div>
          </div>
          <div>
            <label className={label}>Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inp} />
          </div>

          <button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2.5 font-medium disabled:opacity-50" data-testid="bid-submit">
            {submitting ? "Submitting…" : "Submit Rate"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-500">{k}</div>
      <div className="text-slate-800">{v}</div>
    </div>
  );
}
